import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';

import { useGetDemoDataSeedJobQuery, useSeedDemoDataMutation } from '../store/hooks';
import type { DemoDataSeedStatusResponse } from '../types';

// Pre-converted bundles sitting in the data-migration bucket. `batchId` MUST
// equal the `--batch-id` baked into that corpus's converted envelopes
// (`source.batch_id`) or every staged row fails with `batch_mismatch`.
interface SeedDataset {
  id: string;
  label: string;
  description: string;
  sourcePrefix: string;
  batchId: string;
  /** Auto-commit default for this dataset; the user can still override it. */
  defaultAutoCommit: boolean;
}

const SEED_DATASETS: SeedDataset[] = [
  {
    id: 'full-caseload',
    label: 'Full caseload',
    description:
      'A synthetic 18-patient caseload (~418 records): encounters, notes, appointments, diagnoses, medications, coverages, and allergies.',
    sourcePrefix: 'v2/seed-corpus/full-caseload/',
    batchId: 'demo-corpus',
    defaultAutoCommit: true,
  },
  {
    id: 'migration-demo',
    label: 'Migration demo (conflicts)',
    description:
      'A small set of patients that produce new, match-conflict, and merge-conflict rows for the data-migration review demo. Import into an org that already has the full caseload; leave auto-commit off to review the conflicts before committing.',
    sourcePrefix: 'v2/seed-corpus/migration-demo/',
    batchId: 'migration-demo',
    defaultAutoCommit: false,
  },
];

const POLL_INTERVAL_MS = 2000;

// Demo seeding writes synthetic records into a live org; it's a lower-environment
// tool only. Never surface it in production or beta.
const HIDDEN_BUILD_ENVS = ['production', 'beta'];
const SEED_BUTTON_HIDDEN = HIDDEN_BUILD_ENVS.includes(
  import.meta.env.VITE_BUILD_ENV as string,
);

// 'staged' is pending in the generated contract (GDN-2398); widen locally
// until types regenerate.
type SeedStatus = DemoDataSeedStatusResponse['status'] | 'staged';

const TERMINAL_STATUSES: SeedStatus[] = [
  'completed',
  'staged',
  'blocked',
  'failed',
];

function isTerminal(status: SeedStatus | undefined): boolean {
  return status !== undefined && TERMINAL_STATUSES.includes(status);
}

function extractError(err: unknown): string {
  const data = (err as { data?: Record<string, unknown> })?.data;
  if (data) {
    const msg = data.message ?? data.detail ?? data.error;
    if (typeof msg === 'string') return msg;
  }
  return 'Could not start the seed. Please try again.';
}

interface DemoDataSeedButtonProps {
  organizationId: string;
  /** Called once when a seed run finishes committing, to refresh the imports list. */
  onSeeded?: () => void;
}

/**
 * Admin-only control that seeds this org with a synthetic demo caseload through
 * the data import pipeline (Lane 2 — GDN-2386): a pre-converted resource bundle is
 * copied into the org's batch, staged, and committed to real records. Starts an
 * async job and polls it for live commit progress.
 *
 * The org must be a demo org (provisioned via Demo Org Provisioning) — the commit
 * attributes clinical records to the org's personas. Seeding an org with no demo
 * personas leaves provider-owned records (notes, encounters, appointments) blocked.
 */
export function DemoDataSeedButton({ organizationId, onSeeded }: DemoDataSeedButtonProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [datasetId, setDatasetId] = useState(SEED_DATASETS[0].id);
  const [autoCommit, setAutoCommit] = useState(SEED_DATASETS[0].defaultAutoCommit);
  const [jobId, setJobId] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const seededFiredForRef = useRef<string | null>(null);

  const dataset =
    SEED_DATASETS.find(d => d.id === datasetId) ?? SEED_DATASETS[0];

  // Picking a dataset resets auto-commit to that dataset's default (migration
  // demo defaults to off so its conflicts land in review); the user can override.
  const handleDatasetChange = (id: string) => {
    const next = SEED_DATASETS.find(d => d.id === id) ?? SEED_DATASETS[0];
    setDatasetId(next.id);
    setAutoCommit(next.defaultAutoCommit);
  };

  const [seedDemoData, { isLoading: isStarting }] = useSeedDemoDataMutation();

  const { data: job } = useGetDemoDataSeedJobQuery(
    { jobId: jobId ?? '' },
    {
      skip: !jobId,
      pollingInterval: polling ? POLL_INTERVAL_MS : 0,
    },
  );

  const status = job?.status;
  const done = isTerminal(status);
  const inFlight = isStarting || (!!jobId && !done);

  // Reset all per-job state when the target org changes. The hosting route
  // (`organization/:organizationId`) isn't remounted on an org switch — React
  // reuses this instance — so without this a job started for one org would keep
  // polling and report its result under a different org's UI.
  useEffect(() => {
    setJobId(null);
    setPolling(false);
    setStartError(null);
    setConfirmOpen(false);
    seededFiredForRef.current = null;
  }, [organizationId]);

  // Stop polling once the job reaches a terminal state.
  useEffect(() => {
    if (isTerminal(status)) setPolling(false);
  }, [status]);

  // Fire onSeeded once when a job reaches `completed`, so the imports list picks
  // up the freshly staged/committed batch.
  useEffect(() => {
    if (status === 'completed' && jobId && seededFiredForRef.current !== jobId) {
      seededFiredForRef.current = jobId;
      onSeeded?.();
    }
  }, [status, jobId, onSeeded]);

  const startSeed = async () => {
    setConfirmOpen(false);
    setStartError(null);
    seededFiredForRef.current = null;
    try {
      const res = await seedDemoData({
        demoDataSeedRequest: {
          organization_id: organizationId,
          source_prefix: dataset.sourcePrefix,
          batch_id: dataset.batchId,
          // auto_commit is pending in the generated contract (GDN-2398)
          ...({ auto_commit: autoCommit } as Record<string, boolean>),
        },
      }).unwrap();
      setJobId(res.job_id);
      setPolling(true);
    } catch (err) {
      setStartError(extractError(err));
    }
  };

  // Hidden in production/beta. Guard sits after all hooks so hook order is stable.
  if (SEED_BUTTON_HIDDEN) return null;

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        border: theme => `1px dashed ${theme.palette.divider}`,
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="subtitle2">Seed demo data</Typography>
          <Typography variant="body2" color="text.secondary">
            Loads a synthetic dataset into this organization through the import
            pipeline — the full demo caseload, or a small migration-demo set
            that produces conflicts to review. For demo orgs only.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={
            inFlight ? <CircularProgress size={16} /> : <AutoAwesome />
          }
          disabled={inFlight}
          onClick={() => setConfirmOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          {inFlight ? 'Seeding…' : done ? 'Seed again' : 'Seed demo data'}
        </Button>
      </Box>

      {startError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {startError}
        </Alert>
      )}

      {job && <SeedProgress job={job} />}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Seed demo data?</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel id="seed-dataset-label">Dataset</InputLabel>
            <Select
              labelId="seed-dataset-label"
              label="Dataset"
              value={datasetId}
              onChange={e => handleDatasetChange(e.target.value)}
            >
              {SEED_DATASETS.map(d => (
                <MenuItem key={d.id} value={d.id}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DialogContentText>{dataset.description}</DialogContentText>

          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={autoCommit}
                onChange={e => setAutoCommit(e.target.checked)}
              />
            }
            label="Commit immediately after staging"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {autoCommit
              ? 'Rows are staged and committed to real records in one pass.'
              : 'Rows are staged and classified, then wait in Data Import for you to review and commit.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={isStarting}
            onClick={() => void startSeed()}
          >
            {autoCommit ? 'Seed & commit' : 'Stage for review'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SeedProgress({ job }: { job: DemoDataSeedStatusResponse }) {
  const { status } = job;

  if (status === 'completed') {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        Seeded {job.committed_count ?? job.total_count ?? 0} records
        {job.staged_count ? ` from ${job.staged_count} staged rows` : ''}.
      </Alert>
    );
  }

  // 'staged' is not yet in the generated status union (backend contract
  // pending for GDN-2398) — compare via string until types regenerate.
  if ((status as string) === 'staged') {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Staged {job.staged_count ?? 0} rows for review — nothing was committed.
        Open Data Import to review the classified rows (new, match, and merge
        conflicts) and commit when ready.
      </Alert>
    );
  }

  if (status === 'blocked') {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Seed blocked: {job.blocked_reason ?? 'unknown reason'}. Confirm this is a
        demo org with provisioned personas and that the corpus bundle exists.
      </Alert>
    );
  }

  if (status === 'failed') {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Seed failed: {job.error ?? 'unknown error'}.
      </Alert>
    );
  }

  // In flight: processing (copy + stage) or committing.
  const committing = status === 'committing';
  const total = job.total_count ?? 0;
  const committed = job.committed_count ?? 0;
  const pct = job.percentage ?? (total > 0 ? (committed / total) * 100 : null);

  const detail = committing
    ? `Committing ${committed}${total ? `/${total}` : ''} records${
        pct != null ? ` (${Math.round(pct)}%)` : ''
      }`
    : job.staged_count
      ? `Staged ${job.staged_count} rows — preparing commit`
      : 'Copying and staging the caseload…';

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" color="text.secondary">
        {detail}
      </Typography>
      <LinearProgress
        variant={committing && pct != null ? 'determinate' : 'indeterminate'}
        value={committing && pct != null ? pct : undefined}
        sx={{ mt: 0.5 }}
      />
    </Box>
  );
}
